/**
 * Pollen Alert Widget
 * 
 * Shows pollen/allergen levels for outdoor worker safety
 * Critical for workers with allergies in the field
 */

import { useState, useEffect } from 'react';
import {
  Box,
  Chip,
  Typography,
  Tooltip,
  CircularProgress,
  Collapse,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from '@mui/material';
import {
  Grass as PollenIcon,
  ExpandLess as CollapseIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useTranslation } from '../i18n/translations';

interface PollenType {
  type: string;
  code: string;
  index: number;
  category: string;
  description: string;
  color: string;
}

interface PollenData {
  overallIndex: number;
  severity: string;
  severityColor: string;
  warning: string;
  date: string;
  pollenTypes: PollenType[];
  plantCount: number;
  inSeasonPlants: Array<{
    name: string;
    index: number;
    category: string;
    color: string;
  }>;
}

interface PollenAlertWidgetProps {
  latitude: number;
  longitude: number;
  darkMode?: boolean;
}

export function PollenAlertWidget({ latitude, longitude, darkMode = true }: PollenAlertWidgetProps) {
  const { t } = useTranslation();
  const [data, setData] = useState<PollenData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!latitude || !longitude) return;

    const fetchPollen = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(
          `/api/google/pollen?lat=${latitude}&lng=${longitude}`
        );
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.details || 'Failed to fetch pollen data');
        }

        const pollenData: PollenData = await response.json();
        setData(pollenData);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    fetchPollen();
    // Refresh every hour
    const interval = setInterval(fetchPollen, 3600000);
    return () => clearInterval(interval);
  }, [latitude, longitude]);

  if (loading) {
    return (
      <Tooltip title={t('pollenLoadingData')}>
        <Chip
          icon={<CircularProgress size={12} sx={{ color: '#a855f7' }} />}
          label={t('pollenLabel')}
          size="small"
          sx={{
            backgroundColor: darkMode ? 'rgba(168, 85, 247, 0.2)' : 'rgba(168, 85, 247, 0.1)',
            color: '#a855f7',
            height: 28,
          }}
        />
      </Tooltip>
    );
  }

  if (error || !data) {
    // Don't show anything if pollen API isn't available
    return null;
  }

  const isHighPollen = data.overallIndex >= 3;

  return (
    <Box sx={{ position: 'relative' }}>
      <Tooltip 
        title={
          <Box>
            <Typography variant="body2" fontWeight={600}>{data.warning}</Typography>
            {data.pollenTypes.length > 0 && (
              <Typography variant="caption">
                {data.pollenTypes.map(p => p.type).join(', ')}
              </Typography>
            )}
          </Box>
        }
      >
        <Chip
          icon={
            isHighPollen ? (
              <WarningIcon sx={{ fontSize: 14, color: data.severityColor }} />
            ) : (
              <PollenIcon sx={{ fontSize: 14, color: data.severityColor }} />
            )
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <span>{t('pollenLabel')}</span>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  backgroundColor: data.severityColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 10,
                  fontWeight: 700,
                  color: '#fff',
                }}
              >
                {data.overallIndex}
              </Box>
            </Box>
          }
          size="small"
          onClick={() => setExpanded(!expanded)}
          sx={{
            backgroundColor: darkMode 
              ? `${data.severityColor}33` 
              : `${data.severityColor}22`,
            color: darkMode ? '#fff' : '#333',
            height: 28,
            cursor: 'pointer',
            border: isHighPollen ? `1px solid ${data.severityColor}` : 'none',
            animation: isHighPollen ? 'pulse 2s infinite' : 'none',
            '@keyframes pulse': {
              '0%, 100%': { opacity: 1 },
              '50%': { opacity: 0.7 },
            },
          }}
        />
      </Tooltip>

      {/* Expanded Details */}
      <Collapse in={expanded}>
        <Paper
          elevation={3}
          sx={{
            position: 'absolute',
            top: 36,
            right: 0,
            width: 260,
            backgroundColor: darkMode ? 'rgba(17, 24, 39, 0.95)' : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: 2,
            border: `1px solid ${darkMode ? 'rgba(168, 85, 247, 0.3)' : 'rgba(0, 0, 0, 0.1)'}`,
            zIndex: 1000,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 1.5,
              backgroundColor: `${data.severityColor}22`,
              borderBottom: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
            }}
          >
            <Typography variant="body2" fontWeight={600} sx={{ color: darkMode ? '#fff' : '#111' }}>
              {data.warning}
            </Typography>
            <Typography variant="caption" sx={{ color: darkMode ? '#9ca3af' : '#666' }}>
              {data.date} • {t('pollenPlantsInSeason', data.plantCount.toString())}
            </Typography>
          </Box>

          {/* Pollen Types */}
          {data.pollenTypes.length > 0 && (
            <List dense sx={{ py: 0 }}>
              {data.pollenTypes.map((pollen) => (
                <ListItem key={pollen.code} sx={{ py: 0.5 }}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="body2" sx={{ color: darkMode ? '#fff' : '#111' }}>
                          {pollen.type}
                        </Typography>
                        <Chip
                          label={pollen.category}
                          size="small"
                          sx={{
                            backgroundColor: pollen.color,
                            color: '#fff',
                            height: 18,
                            fontSize: 10,
                          }}
                        />
                      </Box>
                    }
                    secondary={
                      <Typography variant="caption" sx={{ color: darkMode ? '#9ca3af' : '#666' }}>
                        {pollen.description}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}

          {/* In-Season Plants */}
          {data.inSeasonPlants.length > 0 && (
            <Box sx={{ p: 1, borderTop: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}` }}>
              <Typography variant="caption" sx={{ color: darkMode ? '#9ca3af' : '#666', fontWeight: 600 }}>
                {t('pollenInSeasonPlants')}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                {data.inSeasonPlants.slice(0, 6).map((plant) => (
                  <Chip
                    key={plant.name}
                    label={plant.name}
                    size="small"
                    sx={{
                      backgroundColor: `${plant.color}33`,
                      color: darkMode ? '#fff' : '#333',
                      height: 20,
                      fontSize: 10,
                    }}
                  />
                ))}
                {data.inSeasonPlants.length > 6 && (
                  <Chip
                    label={t('pollenMorePlants', (data.inSeasonPlants.length - 6).toString())}
                    size="small"
                    sx={{
                      backgroundColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
                      color: darkMode ? '#9ca3af' : '#666',
                      height: 20,
                      fontSize: 10,
                    }}
                  />
                )}
              </Box>
            </Box>
          )}

          {/* Close Button */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              borderTop: `1px solid ${darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
            }}
          >
            <IconButton
              size="small"
              onClick={() => setExpanded(false)}
              sx={{ color: darkMode ? '#9ca3af' : '#666' }}
            >
              <CollapseIcon />
            </IconButton>
          </Box>
        </Paper>
      </Collapse>
    </Box>
  );
}
